import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import {
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import { Label } from '@/components/ui/catalyst/fieldset';
import { Switch, SwitchField } from '@/components/ui/catalyst/switch';
import { cn } from '@/lib/utils';
import {
  useDeleteAgentChatWorkspaceExhibitMutation,
  useGetAgentChatWorkspaceQuery,
  useListAgentChatWorkspaceExhibitsQuery,
  useUpdateAgentChatWorkspaceExhibitMutation,
  useUpdateAgentChatWorkspaceMutation,
} from '@/services/api/agentChatWorkspaceService';
import {
  AgentChatWorkspace,
  AgentChatWorkspaceExhibitGenerationStatus,
} from '@/services/types/agent-chat-workspace-types';

import { ExhibitsList } from './ExhibitsList';
import { MultiExhibitUploadDialog } from './multi-upload';

interface WorkspaceExhibitsProps {
  workspace: AgentChatWorkspace;
  matterId: string;
  className?: string;
}

export const WorkspaceExhibits = ({
  workspace,
  matterId,
  className,
}: WorkspaceExhibitsProps) => {
  // API hooks
  const {
    data: exhibits = [],
    isLoading,
    error,
    refetch,
  } = useListAgentChatWorkspaceExhibitsQuery({
    matterId,
    workspaceId: workspace.id,
  });

  // Fetch the latest workspace data to get download URL and status
  const { data: latestWorkspace, isLoading: isWorkspaceLoading } =
    useGetAgentChatWorkspaceQuery(
      {
        matterId,
        workspaceId: workspace.id,
      },
      { pollingInterval: 5000, skipPollingIfUnfocused: true },
    );

  const [updateExhibit] = useUpdateAgentChatWorkspaceExhibitMutation();
  const [deleteExhibit] = useDeleteAgentChatWorkspaceExhibitMutation();
  const [updateWorkspace] = useUpdateAgentChatWorkspaceMutation();

  const handleUpdateExhibitName = async (
    exhibitId: string,
    newName: string,
  ) => {
    try {
      await updateExhibit({
        matterId,
        workspaceId: workspace.id,
        exhibitId,
        data: {
          name: newName.trim(),
        },
      }).unwrap();

      toast.success('Exhibit name updated');
    } catch (err) {
      console.error('Failed to update exhibit name:', err);
      toast.error('Failed to update exhibit name');
    }
  };

  const handleReorderExhibits = async (fromIndex: number, toIndex: number) => {
    // Optimistic update for immediate UI feedback
    try {
      // Update the index for each affected exhibit
      const exhibitToMove = exhibits[fromIndex];
      await updateExhibit({
        matterId,
        workspaceId: workspace.id,
        exhibitId: exhibitToMove.id,
        data: {
          name: exhibitToMove.name,
          index: toIndex,
        },
      }).unwrap();

      // Refetch to get the updated order from server
      refetch();
    } catch (err) {
      console.error('Failed to reorder exhibits:', err);
      toast.error('Failed to reorder exhibits');
      // The refetch will restore the original order
      refetch();
    }
  };

  const handleDeleteExhibit = async (exhibitId: string) => {
    try {
      await deleteExhibit({
        matterId,
        workspaceId: workspace.id,
        exhibitId,
      }).unwrap();

      toast.success('Exhibit deleted');
    } catch (err) {
      console.error('Failed to delete exhibit:', err);
      toast.error('Failed to delete exhibit');
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = exhibits.findIndex(
        (exhibit) => exhibit.id === active.id,
      );
      const newIndex = exhibits.findIndex((exhibit) => exhibit.id === over.id);

      handleReorderExhibits(oldIndex, newIndex);
    }
  };

  const handleDownloadExhibits = () => {
    if (latestWorkspace?.exhibit_pdf_download_url) {
      // Open PDF in a new tab
      window.open(latestWorkspace.exhibit_pdf_download_url, '_blank');
      toast.success('Opening PDF in new tab');
    } else {
      toast.error('Download not available');
    }
  };

  const handleToggleDisableExhibitPages = async () => {
    const newValue = !latestWorkspace?.disable_exhibit_pages;
    try {
      await updateWorkspace({
        matterId,
        workspaceId: workspace.id,
        data: {
          name: workspace.name,
          disable_exhibit_pages: newValue,
          exhibit_label_format: workspace.exhibit_label_format,
        },
      }).unwrap();

      toast.success(
        newValue ? 'Exhibit pages disabled' : 'Exhibit pages enabled',
      );
    } catch (err) {
      console.error('Failed to update workspace:', err);
      toast.error('Failed to update workspace');
    }
  };

  const sortedExhibits = useMemo(() => {
    return [...exhibits].sort((a, b) => a.order - b.order);
  }, [exhibits]);

  // Determine download button state
  const isDownloadReady =
    latestWorkspace?.exhibit_pdf_generation_status ===
    AgentChatWorkspaceExhibitGenerationStatus.COMPLETED;

  const isGenerating =
    latestWorkspace?.exhibit_pdf_generation_status ===
      AgentChatWorkspaceExhibitGenerationStatus.PENDING ||
    latestWorkspace?.exhibit_pdf_generation_status ===
      AgentChatWorkspaceExhibitGenerationStatus.GENERATING;

  const isFailed =
    latestWorkspace?.exhibit_pdf_generation_status ===
    AgentChatWorkspaceExhibitGenerationStatus.FAILED;

  const showSpinner = isGenerating || isWorkspaceLoading;

  if (error) {
    return (
      <div className={cn('flex h-full w-full flex-col', className)}>
        <div className="flex items-center justify-center flex-1">
          <div className="text-center text-red-600">
            <p className="text-sm font-medium">Failed to load exhibits</p>
            <Button onClick={() => refetch()} className="mt-2" plain>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex h-full w-full flex-col', className)}>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {workspace.name} - Exhibits
          </h2>
          <p className="text-sm text-gray-500">
            {isLoading
              ? 'Loading...'
              : `${exhibits.length} exhibit${exhibits.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Toggle for disabling exhibit pages */}
          <SwitchField>
            <Label>Disable exhibit pages</Label>
            <Switch
              name="disable_exhibit_pages"
              checked={latestWorkspace?.disable_exhibit_pages ?? false}
              onChange={handleToggleDisableExhibitPages}
            />
          </SwitchField>

          <MultiExhibitUploadDialog
            matterId={matterId}
            workspaceId={workspace.id}
            existingExhibitBlobIds={exhibits.map((exhibit) => exhibit.blob)}
            existingExhibitCount={exhibits.length}
            onSuccess={refetch}
          />

          {/* Download Combined Exhibits Button */}
          <Button
            onClick={handleDownloadExhibits}
            disabled={!isDownloadReady || exhibits.length === 0}
            className="gap-1.5"
            color="blue"
          >
            {showSpinner ? (
              <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
            ) : (
              <ArrowDownTrayIcon className="h-4 w-4" />
            )}
            {showSpinner ? 'Generating...' : 'Download PDF'}
          </Button>
        </div>
      </div>

      {/* Error/Warning Banner */}
      {isFailed && latestWorkspace?.exhibit_pdf_generation_error && (
        <div className="mx-6 mt-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-red-600" />
          <div className="text-sm text-red-800">
            <p className="font-medium">Exhibit PDF generation failed</p>
            <p className="mt-1">{latestWorkspace.exhibit_pdf_generation_error}</p>
          </div>
        </div>
      )}
      {isDownloadReady && latestWorkspace?.exhibit_pdf_generation_error && (
        <div className="mx-6 mt-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">Some exhibits were skipped</p>
            <p className="mt-1">{latestWorkspace.exhibit_pdf_generation_error}</p>
          </div>
        </div>
      )}

      {/* Exhibits List with Drag and Drop */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="divide-y divide-gray-100">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="flex items-center gap-4 p-4 animate-pulse"
              >
                <div className="h-8 w-8 bg-gray-200 rounded" />
                <div className="h-8 w-8 bg-gray-200 rounded-full" />
                <div className="h-5 w-5 bg-gray-200 rounded" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </div>
                <div className="flex gap-1">
                  <div className="h-6 w-6 bg-gray-200 rounded" />
                  <div className="h-6 w-6 bg-gray-200 rounded" />
                  <div className="h-8 w-8 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <ExhibitsList
              exhibits={sortedExhibits}
              labelFormat={latestWorkspace?.exhibit_label_format}
              onUpdateName={handleUpdateExhibitName}
              onReorder={handleReorderExhibits}
              onDelete={handleDeleteExhibit}
            />
          </DndContext>
        )}
      </div>
    </div>
  );
};
